import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { guides, type GuideDefinition } from '../data/guides';
import { tools, type ToolSlug } from '../data/tools';
import { Seo } from '../lib/seo';
import { absoluteUrl } from '../lib/site';

const symptomPaths = [
  { label: 'My photo is blurry', description: 'Separate motion blur, camera shake and ordinary softness.', to: '/guides/fix-motion-blur-and-soft-focus' },
  { label: 'My image is grainy', description: 'Reduce low-light noise without erasing useful texture.', to: '/guides/remove-noise-from-low-light-photos' },
  { label: 'My picture is too small', description: 'Calculate the pixels you need before choosing 2×, 4× or 8×.', to: '/guides/increase-image-resolution-without-losing-quality' },
  { label: 'I need to remove something', description: 'Choose object removal, text removal or a broader prompt-based edit.', to: '/guides/object-remover-practical-workflow' },
  { label: 'I need more canvas', description: 'Learn when generative expand is better than cropping or stretching.', to: '/guides/image-extender-practical-workflow' },
  { label: 'I need a smaller web image', description: 'Resize and compress without spending AI credits.', to: '/guides/compress-practical-workflow' }
] as const;

const guideFamilies: { id: string; label: string; description: string; slugs: ToolSlug[] }[] = [
  {
    id: 'enhance-repair',
    label: 'Enhance, upscale & repair',
    description: 'Guides for low-quality photos, blur, noise, sharpening, old-photo restoration and face detail.',
    slugs: ['enhance', 'upscale', 'restore', 'face-restore', 'deblur', 'denoise', 'sharpen']
  },
  {
    id: 'ai-editing',
    label: 'AI editing & generative changes',
    description: 'Object cleanup, text removal, relighting, image extension, background changes, colorization and prompt editing.',
    slugs: ['relight', 'object-remover', 'text-remover', 'image-extender', 'background-changer', 'photo-colorizer', 'image-editor']
  },
  {
    id: 'design-commerce',
    label: 'Design & commerce workflows',
    description: 'Transparent cutouts, SVG vectorization and accurate product-photo preparation for online publishing.',
    slugs: ['remove-background', 'vectorize', 'product-photo']
  },
  {
    id: 'free-utilities',
    label: 'Free image utilities',
    description: 'No-credit guides for image compression, resizing, cropping, conversion, social sizes, color work and metadata-clean exports.',
    slugs: ['compress', 'resize', 'crop', 'convert', 'social-resize', 'color-picker', 'palette-generator', 'metadata-remover']
  }
];

function GuideCard({ guide, index }: { guide: GuideDefinition; index: number }) {
  const Icon = guide.icon;
  return (
    <article className="guide-library-card">
      <div className="guide-card-head">
        <div className="guide-card-title-row">
          <span className="guide-number">{String(index + 1).padStart(2, '0')}</span>
          <h3>{guide.title}</h3>
        </div>
        <span className="tool-icon guide-card-icon"><Icon size={20} /></span>
      </div>
      <p className="eyebrow guide-card-category">{guide.category}</p>
      <p className="guide-card-description">{guide.description}</p>
      <Link className="inline-link" to={`/guides/${guide.slug}`}>Read guide <ArrowRight size={16} /></Link>
    </article>
  );
}

export function GuidesPage() {
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Nexhance AI image editing and image utility guides',
    itemListElement: guides.map((guide, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: guide.title,
      url: absoluteUrl(`/guides/${guide.slug}`)
    }))
  };

  const featuredGuides = guides.slice(0, 12);

  return (
    <section className="page-section guides-page">
      <Seo
        title="AI Image Editing, Upscaling & Free Image Tool Guides"
        description="Practical guides for AI image enhancement, upscaling, restoration, object removal, generative expand, background editing, image compression, resizing, WebP conversion and social media image sizes."
        path="/guides"
        jsonLd={itemListSchema}
      />
      <div className="shell">
        <div className="page-hero compact-page-hero">
          <p className="eyebrow">Practical image knowledge base</p>
          <h1>Use the right image tool, understand the trade-offs, and know what to check before publishing.</h1>
          <p>Nexhance AI guides focus on the image problems and delivery decisions that benefit from a real explanation, from enhancement and restoration to generative editing, compression, resizing and format conversion.</p>
        </div>

        <section className="guide-symptom-section" aria-labelledby="guide-symptom-heading">
          <div className="guide-symptom-heading">
            <div><p className="eyebrow">Start with the problem</p><h2 id="guide-symptom-heading">Find help by what you need to fix.</h2></div>
            <p>Search intent is useful only when it maps to a real task. These starting points lead to a diagnosis or workflow rather than a page created only around a keyword.</p>
          </div>
          <div className="guide-symptom-grid">
            {symptomPaths.map((item) => <Link key={item.to} to={item.to}><strong>{item.label}</strong><span>{item.description}</span><small>Read the guide <ArrowRight size={14} /></small></Link>)}
          </div>
        </section>

        <section className="guide-learning-paths" aria-labelledby="guide-learning-paths-heading">
          <div className="guide-category-heading">
            <div><p className="eyebrow">Three useful learning paths</p><h2 id="guide-learning-paths-heading">Follow the image from problem to delivery.</h2></div>
            <p>A strong workflow usually starts with diagnosis, uses one specialist correction, then finishes with the correct dimensions, file format and quality review.</p>
          </div>
          <div className="guide-learning-path-grid">
            <article>
              <span>01</span><h3>Repair a difficult photo</h3>
              <Link to="/guides/fix-blurry-noisy-low-resolution-photos">Diagnose the defect</Link>
              <Link to="/guides/denoise-vs-deblur-vs-sharpen">Choose the repair tool</Link>
              <Link to="/guides/increase-image-resolution-without-losing-quality">Upscale only if needed</Link>
            </article>
            <article>
              <span>02</span><h3>Edit or rebuild part of an image</h3>
              <Link to="/guides/object-remover-comparison">Object remover vs general editor</Link>
              <Link to="/guides/image-extender-common-mistakes">Avoid bad generative expansion</Link>
              <Link to="/guides/background-changer-quality-review">Review edges and subject accuracy</Link>
            </article>
            <article>
              <span>03</span><h3>Prepare images for the web</h3>
              <Link to="/guides/resize-practical-workflow">Choose useful dimensions</Link>
              <Link to="/guides/compress-web-social-export">Compress for real display size</Link>
              <Link to="/guides/convert-practical-workflow">Choose JPG, PNG, WebP or AVIF</Link>
            </article>
          </div>
        </section>

        <section className="guide-featured-section" aria-labelledby="featured-guides-heading">
          <div className="guide-category-heading">
            <div><p className="eyebrow">Featured editorial guides</p><h2 id="featured-guides-heading">Long-form answers to common image-quality questions.</h2></div>
            <p>These are the deeper guides that connect several tools, explain limits and help with decisions that cannot be answered by a one-line tool description.</p>
          </div>
          <div className="guide-grid expanded-guide-grid">
            {featuredGuides.map((guide, index) => <GuideCard key={guide.slug} guide={guide} index={index} />)}
          </div>
        </section>

        <nav className="guide-topic-row guide-category-nav" aria-label="Guide families">
          {guideFamilies.map((family) => <a key={family.id} href={`#${family.id}`}>{family.label}</a>)}
        </nav>

        <div className="guide-tool-directory">
          {guideFamilies.map((family) => (
            <section className="guide-family-section" id={family.id} key={family.id}>
              <header className="guide-category-heading">
                <div><p className="eyebrow">Guide library</p><h2>{family.label}</h2></div>
                <p>{family.description}</p>
              </header>
              <div className="guide-tool-clusters">
                {family.slugs.map((slug) => {
                  const tool = tools.find((item) => item.slug === slug);
                  if (!tool) return null;
                  const toolGuides = guides.filter((guide) => guide.relatedTool === slug);
                  if (!toolGuides.length) return null;
                  const Icon = tool.icon;
                  return (
                    <article className="guide-tool-cluster" key={slug}>
                      <div className="guide-tool-cluster-head">
                        <span className="tool-icon"><Icon size={20} /></span>
                        <div><p className="eyebrow">{tool.mode === 'free' ? 'Free · 0 credits' : 'AI workflow'}</p><h3>{tool.name}</h3></div>
                        <Link to={`/tools/${tool.slug}`}>Open tool <ArrowRight size={15} /></Link>
                      </div>
                      <p>{tool.description}</p>
                      <div className="guide-tool-link-list">
                        {toolGuides.map((guide) => <Link key={guide.slug} to={`/guides/${guide.slug}`}><span>{guide.title}</span><ArrowRight size={14} /></Link>)}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <section className="guides-search-intent-copy">
          <header className="guides-search-intent-heading">
            <p className="eyebrow">People-first SEO</p>
            <h2>Search-friendly guides should still answer a real image question.</h2>
          </header>
          <div className="guides-search-intent-body">
            <p>People search with phrases such as AI image editor, AI image enhancer, image upscaler, object remover, background changer, generative expand, image compressor, WebP converter and social media image resizer. Nexhance AI uses those terms when they accurately describe the task, then expands the article around source preparation, settings, quality checks, limitations and the final destination.</p>
            <p>The expanded library gives every tool a deeper learning path without showing repetitive reading-time badges. Each guide is built around a distinct workflow question, comparison, troubleshooting problem or delivery decision, so the title and useful content carry the visual hierarchy instead of metadata that made every card look the same.</p>
          </div>
        </section>

        <div className="guide-cta">
          <div><p className="eyebrow">Ready to work?</p><h2>Choose the image problem and open its purpose-built workflow.</h2></div>
          <Link className="button button-primary button-large" to="/tools">Explore all 25 tools <ArrowRight size={18} /></Link>
        </div>
      </div>
    </section>
  );
}
